import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { SportIcon } from '../../../src/sports/components/SportIcon/SportIcon';
import { getRaceCardIdentityStyles } from './RaceCardIdentity.styles';
import { RACE_CARD_IDENTITY_TEST_IDS } from './RaceCardIdentity.constants';
import type { Game } from '../../../src/create/types/create.types';

export interface RaceCardIdentityProps {
  game: Game;
}

/**
 * The identity block of a FIELD fixture's card (specs/formula-one-challenges).
 *
 * Replaces the two-team column for a sport that has no two teams. A Grand Prix is identified by
 * the event and the circuit — "Netherlands Grand Prix / Circuit Zandvoort" — not by a pairing, so
 * rendering it through the home/away column would print the event name twice, or "Unknown vs
 * Unknown" on any payload that predates the field columns.
 *
 * Kept in its own file rather than as another branch inside `GameInlineCard`: that component
 * already carries the expansion, walkthrough and pick-state logic, and none of it differs for a
 * race. Only the identity does.
 */
export const RaceCardIdentity: React.FC<RaceCardIdentityProps> = ({ game }) => {
  const styles = useMemo(() => getRaceCardIdentityStyles(), []);

  // `eventName` is absent on a payload from a server that predates the field columns; the card
  // still has to render something, and the list already sorts by it.
  const eventName = game.eventName ?? game.homeTeam;
  const circuit = game.venue;

  return (
    <View style={styles.column} testID={RACE_CARD_IDENTITY_TEST_IDS.root}>
      <View style={styles.titleRow}>
        {/* Decorative: the event name beside it already says which sport this is. */}
        <SportIcon sport={game.sport} size={18} decorative />
        <Text
          style={styles.eventName}
          numberOfLines={1}
          testID={RACE_CARD_IDENTITY_TEST_IDS.eventName}
        >
          {eventName}
        </Text>
      </View>
      {circuit != null && circuit !== '' && (
        <Text
          style={styles.circuit}
          numberOfLines={1}
          testID={RACE_CARD_IDENTITY_TEST_IDS.circuit}
        >
          {circuit}
        </Text>
      )}
    </View>
  );
};
